import type { MNDrawingContent } from '../../../../../common/elements/elementModelTypes';
import { PATH_TAG_REGEX, tryTransformSvgPathData } from '../../../drawing/drawingEditor/utils/drawingSvgPathUtils';

type DrawingTransform = {
    rotation: number; // degrees, e.g. -90, -180, -270, 0 (can be negative)
    scaleX: number; // 1 or -1
    scaleY: number; // 1 or -1
};

const PATH_DATA_ATTRIBUTE_REGEX = /\sd\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;

const getFlippedPathData = ({
    pathData,
    width,
    height,
    scaleX,
    scaleY,
}: {
    pathData: string;
    width: number;
    height: number;
    scaleX: number;
    scaleY: number;
}) =>
    tryTransformSvgPathData({
        pathData,
        scaleX,
        scaleY,
        translation: {
            x: scaleX === -1 ? width : 0,
            y: scaleY === -1 ? height : 0,
        },
    });

/**
 * Bake a flip into each SVG path so the drawing editor rehydrates the same geometry the SVG renders.
 */
const transformDrawingSvgPaths = ({
    svg,
    width,
    height,
    scaleX,
    scaleY,
}: {
    svg: string;
    width: number;
    height: number;
    scaleX: number;
    scaleY: number;
}): { svg: string; transformedAllPaths: boolean } => {
    let transformedPathCount = 0;
    let failedPathCount = 0;
    const transformedSvg = svg.replace(PATH_TAG_REGEX, (pathTag) =>
        pathTag.replace(PATH_DATA_ATTRIBUTE_REGEX, (attribute, quotedValue, doubleQuotedValue, singleQuotedValue) => {
            const pathData = doubleQuotedValue || singleQuotedValue || quotedValue;
            const quote = singleQuotedValue !== undefined ? "'" : '"';
            const flippedPathData = getFlippedPathData({ pathData, width, height, scaleX, scaleY });

            if (!flippedPathData) {
                failedPathCount += 1;
                return attribute;
            }

            transformedPathCount += 1;
            return ` d=${quote}${flippedPathData}${quote}`;
        }),
    );

    return {
        svg: transformedSvg,
        transformedAllPaths: transformedPathCount > 0 && failedPathCount === 0,
    };
};

/**
 * Bake a rotation + flip transform into a stored drawing SVG so it stays aligned
 * with a transformed image. Flips are baked into path data so the drawing editor
 * rehydrates the same geometry that read-only SVG rendering shows.
 */
export const transformDrawingContent = (
    drawing: MNDrawingContent,
    { rotation, scaleX, scaleY }: DrawingTransform,
): MNDrawingContent => {
    const w = drawing.width ?? 0;
    const h = drawing.height ?? 0;

    if (!drawing.svg || !w || !h) {
        return drawing;
    }

    if (rotation === 0) {
        const transformedDrawingSvg = transformDrawingSvgPaths({
            svg: drawing.svg,
            width: w,
            height: h,
            scaleX,
            scaleY,
        });

        if (!transformedDrawingSvg.transformedAllPaths) return drawing;

        return {
            ...drawing,
            svg: transformedDrawingSvg.svg,
            paths: [],
        };
    }

    // Normalise rotation to 0, 90, 180, or 270
    const norm = ((rotation % 360) + 360) % 360;

    // For quarter-turn rotations (90 or 270), swap width and height
    const isQuarterTurn = norm === 90 || norm === 270;
    const newW = isQuarterTurn ? h : w;
    const newH = isQuarterTurn ? w : h;

    // Build the SVG group transform string (SVG transforms are written outermost-first)
    const transform = `translate(${newW / 2} ${newH / 2}) rotate(${rotation}) scale(${scaleX} ${scaleY}) translate(${-w / 2} ${-h / 2})`;

    // Parse the SVG to wrap content in a <g transform>
    const match = drawing.svg.match(/^(<svg[^>]*>)([\s\S]*)(<\/svg>)$/);
    if (!match) {
        return drawing;
    }

    // Replace the viewBox with the new dimensions
    const openTag = match[1].replace(/viewBox="[^"]*"/, `viewBox="0 0 ${newW} ${newH}"`);
    const newSvg = `${openTag}<g transform="${transform}">${match[2]}</g>${match[3]}`;

    return { ...drawing, svg: newSvg, width: newW, height: newH, paths: [] };
};
